/**
 * Class Parser Service
 * Parses class files to extract subject-teacher assignments
 * One module for one parsing service - following the app's architecture rule
 */

import { supabase } from '../lib/supabase';
import TeacherAssignmentsService from './TeacherAssignmentsService';

class ClassParserService {
  constructor() {
    this.teacherCodeMapping = null;
  }

  /**
   * Initialize teacher code mapping
   * @param {string} tenantId - Tenant ID
   */
  async initializeTeacherMapping(tenantId) {
    try {
      this.teacherCodeMapping = await TeacherAssignmentsService.getTeacherCodeMapping(tenantId);
      console.log('Teacher code mapping initialized:', Object.keys(this.teacherCodeMapping).length, 'teachers');
    } catch (error) {
      console.error('Error initializing teacher mapping:', error);
      throw error;
    }
  }

  /**
   * Parse a single class file content
   * @param {string} classContent - Class file content
   * @param {string} className - Class name (e.g., "Class 1A")
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Array>} Array of parsed assignments
   */
  async parseClassFile(classContent, className, tenantId) {
    console.log(`Starting to parse class file: ${className}`);
    console.log('Class content preview:', classContent.substring(0, 200));
    
    if (!this.teacherCodeMapping) {
      console.log('Initializing teacher mapping...');
      await this.initializeTeacherMapping(tenantId);
    }

    console.log('Teacher code mapping keys:', Object.keys(this.teacherCodeMapping));
    console.log('Available teacher codes:', Object.keys(this.teacherCodeMapping));

    // Get existing assignments to avoid duplicates
    const existingAssignments = await this.getExistingAssignments(tenantId, className);
    console.log(`Found ${existingAssignments.length} existing assignments for ${className}`);

    const assignments = [];
    const lines = classContent.split('\n');
    
    let currentSection = null;
    let classMaster = null;
    let viceClassMaster = null;
    let classroom = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines
      if (!line) continue;

      // Parse class header
      if (line.startsWith('###')) {
        // Already have className parameter
        continue;
      }

      // Parse class master and vice class master
      if (line.includes('班主任') && line.includes(':')) {
        const match = line.match(/班主任[^:]*[：:]\s*(.+)/);
        if (match) {
          classMaster = match[1].trim().replace(/^\*\*\s*/, '').replace(/\s*\*\*$/, '');
        }
      }

      if (line.includes('副班主任') && line.includes(':')) {
        const match = line.match(/副班主任[^:]*[：:]\s*(.+)/);
        if (match) {
          viceClassMaster = match[1].trim().replace(/^\*\*\s*/, '').replace(/\s*\*\*$/, '');
        }
      }

      // Parse classroom
      if (line.includes('課室') && line.includes(':')) {
        const match = line.match(/課室[：:]\s*(.+)/);
        if (match) {
          classroom = match[1].trim();
        }
      }

      // Parse subjects and teachers
      if (line.includes(':') && !line.includes('班主任') && !line.includes('課室')) {
        const subjectMatch = line.match(/^\s*-\s*(.+?):\s*(.+)$/) || line.match(/^\s*(.+?):\s*(.+)$/);
        if (subjectMatch) {
          const subjectName = subjectMatch[1].trim().replace(/^\*\*\s*/, '').replace(/\s*\*\*$/, '');
          const teacherCodes = subjectMatch[2].trim().replace(/^\*\*\s*/, '').replace(/\s*\*\*$/, '');

          // Skip empty assignments
          if (teacherCodes === '(空)' || teacherCodes === '') {
            continue;
          }

          // Parse multiple teachers (separated by /, space, or comma)
          const teachers = this.parseTeacherCodes(teacherCodes);
          
          for (const teacherCode of teachers) {
            const teacher = this.teacherCodeMapping[teacherCode];
            if (teacher) {
              const assignment = {
                tenant_id: tenantId,
                teacher_id: teacher.id,
                assignment_type: 'subject_teacher',
                academic_year: '2025-26',
                term: '1',
                active_from: '2025-09-01',
                active_until: null,
                is_primary: false,
                responsibilities: [`Teaching ${subjectName} to ${className}`],
                notes: `Parsed from class file: ${className}`,
                class_id: className,
                subject_name: subjectName,
                eca_id: null,
                committee_name: null,
                duty_name: null,
                duty_type: null,
                duty_location: classroom,
                duty_schedule: null,
                weekdays: null,
                start_time: null,
                end_time: null,
                frequency: null,
                rotation_cycle: null,
                member_role: null,
                role_in_activity: null,
                weekly_periods: null,
                curriculum_code: null,
                teaching_level: this.extractFormLevel(className),
                co_teacher_id: null,
                backup_teacher_id: null,
                performance_rating: null,
                student_performance_rating: null,
                parent_satisfaction_rating: null,
                attendance_rate: null,
                contribution_rating: null,
                assignment_data: {
                  source: 'class_file_parser',
                  class_name: className,
                  teacher_code: teacherCode,
                  teacher_name: teacher.name,
                  classroom: classroom
                },
                metadata: {
                  parsed_at: new Date().toISOString(),
                  source_file: `${className}.txt`
                }
              };

              // Check for duplicates
              const isDuplicate = this.isDuplicateAssignment(assignment, existingAssignments);
              assignment.isDuplicate = isDuplicate;
              
              assignments.push(assignment);
            } else {
              console.warn(`Teacher code "${teacherCode}" not found for subject "${subjectName}" in ${className} - creating assignment for manual update`);
              
              // Create assignment with unknown teacher for manual update
              const assignment = {
                tenant_id: tenantId,
                teacher_id: null, // Will be set when user updates
                assignment_type: 'subject_teacher',
                academic_year: '2025-26',
                term: '1',
                active_from: '2025-09-01',
                active_until: null,
                is_primary: false,
                responsibilities: [`Teaching ${subjectName} to ${className}`],
                notes: `Parsed from class file: ${className} - Teacher code "${teacherCode}" not found, needs manual update`,
                class_id: className,
                subject_name: subjectName,
                eca_id: null,
                committee_name: null,
                duty_name: null,
                duty_type: null,
                duty_location: classroom,
                duty_schedule: null,
                weekdays: null,
                start_time: null,
                end_time: null,
                frequency: null,
                rotation_cycle: null,
                member_role: null,
                role_in_activity: null,
                weekly_periods: null,
                curriculum_code: null,
                teaching_level: this.extractFormLevel(className),
                co_teacher_id: null,
                backup_teacher_id: null,
                performance_rating: null,
                student_performance_rating: null,
                parent_satisfaction_rating: null,
                attendance_rate: null,
                contribution_rating: null,
                assignment_data: {
                  source: 'class_file_parser',
                  class_name: className,
                  teacher_code: teacherCode,
                  teacher_name: 'Unknown Teacher',
                  classroom: classroom,
                  needs_update: true,
                  original_teacher_code: teacherCode
                },
                metadata: {
                  parsed_at: new Date().toISOString(),
                  source_file: `${className}.txt`,
                  needs_manual_update: true
                }
              };

              // Check for duplicates
              const isDuplicate = this.isDuplicateAssignment(assignment, existingAssignments);
              assignment.isDuplicate = isDuplicate;
              
              assignments.push(assignment);
            }
          }
        }
      }
    }

    // Add class master assignment
    if (classMaster) {
      const classMasterTeacher = this.teacherCodeMapping[classMaster];
      if (classMasterTeacher) {
        const assignment = {
          tenant_id: tenantId,
          teacher_id: classMasterTeacher.id,
          assignment_type: 'class_master',
          academic_year: '2025-26',
          term: '1',
          active_from: '2025-09-01',
          active_until: null,
          is_primary: true,
          responsibilities: [`Class Master for ${className}`],
          notes: `Parsed from class file: ${className}`,
          class_id: className,
          subject_name: null,
          eca_id: null,
          committee_name: null,
          duty_name: null,
          duty_type: null,
          duty_location: classroom,
          duty_schedule: null,
          weekdays: null,
          start_time: null,
          end_time: null,
          frequency: null,
          rotation_cycle: null,
          member_role: 'Class Master',
          role_in_activity: null,
          weekly_periods: null,
          curriculum_code: null,
          teaching_level: this.extractFormLevel(className),
          co_teacher_id: null,
          backup_teacher_id: null,
          performance_rating: null,
          student_performance_rating: null,
          parent_satisfaction_rating: null,
          attendance_rate: null,
          contribution_rating: null,
          assignment_data: {
            source: 'class_file_parser',
            teacher_code: classMaster,
            teacher_name: classMasterTeacher.name,
            classroom: classroom
          },
          metadata: {
            parsed_at: new Date().toISOString(),
            source_file: `${className}.txt`
          }
        };

        // Check for duplicates
        const isDuplicate = this.isDuplicateAssignment(assignment, existingAssignments);
        assignment.isDuplicate = isDuplicate;
        
        assignments.push(assignment);
      } else {
        console.warn(`Class Master teacher code "${classMaster}" not found for ${className} - creating assignment for manual update`);
        
        // Create assignment with unknown teacher for manual update
        const assignment = {
          tenant_id: tenantId,
          teacher_id: null, // Will be set when user updates
          assignment_type: 'class_master',
          academic_year: '2025-26',
          term: '1',
          active_from: '2025-09-01',
          active_until: null,
          is_primary: true,
          responsibilities: [`Class Master for ${className}`],
          notes: `Parsed from class file: ${className} - Teacher code "${classMaster}" not found, needs manual update`,
          class_id: className,
          subject_name: null,
          eca_id: null,
          committee_name: null,
          duty_name: null,
          duty_type: null,
          duty_location: classroom,
          duty_schedule: null,
          weekdays: null,
          start_time: null,
          end_time: null,
          frequency: null,
          rotation_cycle: null,
          member_role: 'Class Master',
          role_in_activity: null,
          weekly_periods: null,
          curriculum_code: null,
          teaching_level: this.extractFormLevel(className),
          co_teacher_id: null,
          backup_teacher_id: null,
          performance_rating: null,
          student_performance_rating: null,
          parent_satisfaction_rating: null,
          attendance_rate: null,
          contribution_rating: null,
          assignment_data: {
            source: 'class_file_parser',
            teacher_code: classMaster,
            teacher_name: 'Unknown Teacher',
            classroom: classroom,
            needs_update: true,
            original_teacher_code: classMaster
          },
          metadata: {
            parsed_at: new Date().toISOString(),
            source_file: `${className}.txt`,
            needs_manual_update: true
          }
        };

        // Check for duplicates
        const isDuplicate = this.isDuplicateAssignment(assignment, existingAssignments);
        assignment.isDuplicate = isDuplicate;
        
        assignments.push(assignment);
      }
    }

    // Add vice class master assignment
    if (viceClassMaster) {
      const viceClassMasterTeacher = this.teacherCodeMapping[viceClassMaster];
      if (viceClassMasterTeacher) {
        const assignment = {
          tenant_id: tenantId,
          teacher_id: viceClassMasterTeacher.id,
          assignment_type: 'vice_class_master',
          academic_year: '2025-26',
          term: '1',
          active_from: '2025-09-01',
          active_until: null,
          is_primary: false,
          responsibilities: [`Vice Class Master for ${className}`],
          notes: `Parsed from class file: ${className}`,
          class_id: className,
          subject_name: null,
          eca_id: null,
          committee_name: null,
          duty_name: null,
          duty_type: null,
          duty_location: classroom,
          duty_schedule: null,
          weekdays: null,
          start_time: null,
          end_time: null,
          frequency: null,
          rotation_cycle: null,
          member_role: 'Vice Class Master',
          role_in_activity: null,
          weekly_periods: null,
          curriculum_code: null,
          teaching_level: this.extractFormLevel(className),
          co_teacher_id: null,
          backup_teacher_id: null,
          performance_rating: null,
          student_performance_rating: null,
          parent_satisfaction_rating: null,
          attendance_rate: null,
          contribution_rating: null,
          assignment_data: {
            source: 'class_file_parser',
            teacher_code: viceClassMaster,
            teacher_name: viceClassMasterTeacher.name,
            classroom: classroom
          },
          metadata: {
            parsed_at: new Date().toISOString(),
            source_file: `${className}.txt`
          }
        };

        // Check for duplicates
        const isDuplicate = this.isDuplicateAssignment(assignment, existingAssignments);
        assignment.isDuplicate = isDuplicate;
        
        assignments.push(assignment);
      } else {
        console.warn(`Vice Class Master teacher code "${viceClassMaster}" not found for ${className} - creating assignment for manual update`);
        
        // Create assignment with unknown teacher for manual update
        const assignment = {
          tenant_id: tenantId,
          teacher_id: null, // Will be set when user updates
          assignment_type: 'vice_class_master',
          academic_year: '2025-26',
          term: '1',
          active_from: '2025-09-01',
          active_until: null,
          is_primary: false,
          responsibilities: [`Vice Class Master for ${className}`],
          notes: `Parsed from class file: ${className} - Teacher code "${viceClassMaster}" not found, needs manual update`,
          class_id: className,
          subject_name: null,
          eca_id: null,
          committee_name: null,
          duty_name: null,
          duty_type: null,
          duty_location: classroom,
          duty_schedule: null,
          weekdays: null,
          start_time: null,
          end_time: null,
          frequency: null,
          rotation_cycle: null,
          member_role: 'Vice Class Master',
          role_in_activity: null,
          weekly_periods: null,
          curriculum_code: null,
          teaching_level: this.extractFormLevel(className),
          co_teacher_id: null,
          backup_teacher_id: null,
          performance_rating: null,
          student_performance_rating: null,
          parent_satisfaction_rating: null,
          attendance_rate: null,
          contribution_rating: null,
          assignment_data: {
            source: 'class_file_parser',
            teacher_code: viceClassMaster,
            teacher_name: 'Unknown Teacher',
            classroom: classroom,
            needs_update: true,
            original_teacher_code: viceClassMaster
          },
          metadata: {
            parsed_at: new Date().toISOString(),
            source_file: `${className}.txt`,
            needs_manual_update: true
          }
        };

        // Check for duplicates
        const isDuplicate = this.isDuplicateAssignment(assignment, existingAssignments);
        assignment.isDuplicate = isDuplicate;
        
        assignments.push(assignment);
      }
    }

    return assignments;
  }

  /**
   * Get existing assignments for a class to avoid duplicates
   * @param {string} tenantId - Tenant ID
   * @param {string} className - Class name
   * @returns {Promise<Array>} Array of existing assignments
   */
  async getExistingAssignments(tenantId, className) {
    try {
      const { data, error } = await supabase
        .from('teacher_assignments')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('class_id', className);

      if (error) {
        console.warn('Error fetching existing assignments:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.warn('Error fetching existing assignments:', error);
      return [];
    }
  }

  /**
   * Check if an assignment already exists
   * @param {Object} assignment - Assignment to check
   * @param {Array} existingAssignments - Array of existing assignments
   * @returns {boolean} True if assignment already exists
   */
  isDuplicateAssignment(assignment, existingAssignments) {
    return existingAssignments.some(existing => 
      existing.teacher_id === assignment.teacher_id &&
      existing.assignment_type === assignment.assignment_type &&
      existing.class_id === assignment.class_id &&
      existing.subject_name === assignment.subject_name
    );
  }

  /**
   * Parse teacher codes from a string
   * @param {string} teacherCodesString - String containing teacher codes
   * @returns {Array} Array of teacher codes
   */
  parseTeacherCodes(teacherCodesString) {
    // Handle various separators: /, space, comma, and combinations
    const codes = teacherCodesString
      .split(/[/\s,]+/)
      .map(code => code.trim())
      .filter(code => code && code !== '' && code !== '(空)');

    return codes;
  }

  /**
   * Extract form level from class name
   * @param {string} className - Class name (e.g., "Class 1A")
   * @returns {string} Form level
   */
  extractFormLevel(className) {
    const match = className.match(/Class\s*(\d+)/);
    if (match) {
      const level = parseInt(match[1]);
      if (level >= 1 && level <= 3) return 'Junior';
      if (level >= 4 && level <= 6) return 'Senior';
    }
    return 'Unknown';
  }

  /**
   * Parse multiple class files
   * @param {Array} classFiles - Array of {content, className} objects
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Array>} Array of all parsed assignments
   */
  async parseMultipleClassFiles(classFiles, tenantId) {
    const allAssignments = [];
    
    for (const classFile of classFiles) {
      try {
        console.log(`Parsing ${classFile.className}...`);
        const assignments = await this.parseClassFile(classFile.content, classFile.className, tenantId);
        allAssignments.push(...assignments);
        console.log(`Parsed ${assignments.length} assignments from ${classFile.className}`);
      } catch (error) {
        console.error(`Error parsing ${classFile.className}:`, error);
      }
    }

    return allAssignments;
  }

  /**
   * Get parsing statistics
   * @param {Array} assignments - Array of assignments
   * @returns {Object} Parsing statistics
   */
  getParsingStatistics(assignments) {
    const stats = {
      totalAssignments: assignments.length,
      byClass: {},
      bySubject: {},
      byTeacher: {},
      byFormLevel: {}
    };

    assignments.forEach(assignment => {
      // By class
      const className = assignment.class_id;
      stats.byClass[className] = (stats.byClass[className] || 0) + 1;

      // By subject
      const subject = assignment.subject_name;
      stats.bySubject[subject] = (stats.bySubject[subject] || 0) + 1;

      // By teacher
      const teacherId = assignment.teacher_id;
      stats.byTeacher[teacherId] = (stats.byTeacher[teacherId] || 0) + 1;

      // By form level
      const formLevel = assignment.teaching_level;
      stats.byFormLevel[formLevel] = (stats.byFormLevel[formLevel] || 0) + 1;
    });

    return stats;
  }

  /**
   * Validate parsed assignments
   * @param {Array} assignments - Array of assignments
   * @returns {Object} Validation result
   */
  validateAssignments(assignments) {
    const errors = [];
    const warnings = [];

    assignments.forEach((assignment, index) => {
      // Check required fields
      if (!assignment.tenant_id) {
        errors.push(`Assignment ${index}: Missing tenant_id`);
      }
      if (!assignment.teacher_id) {
        errors.push(`Assignment ${index}: Missing teacher_id`);
      }
      if (!assignment.subject_name) {
        errors.push(`Assignment ${index}: Missing subject_name`);
      }
      if (!assignment.class_id) {
        errors.push(`Assignment ${index}: Missing class_id`);
      }

      // Check for duplicate assignments
      const duplicates = assignments.filter(a => 
        a.teacher_id === assignment.teacher_id && 
        a.subject_name === assignment.subject_name && 
        a.class_id === assignment.class_id
      );
      if (duplicates.length > 1) {
        warnings.push(`Duplicate assignment: ${assignment.teacher_id} teaching ${assignment.subject_name} in ${assignment.class_id}`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      totalAssignments: assignments.length,
      errorCount: errors.length,
      warningCount: warnings.length
    };
  }
}

export default new ClassParserService();
